import type { ReactElement } from 'react';
import { Dna } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface TenantLogoProps {
  /**
   * URL to a tenant-specific logo SVG/PNG. When ``null`` we fall back to the
   * generic PhenoTips glyph. The component intentionally does NOT fetch the
   * logo itself; callers derive the URL from tenant config.
   */
  readonly src?: string | null;
  readonly tenantName?: string | null;
  readonly className?: string;
}

/**
 * Displays the tenant-branded header logo with a predictable default when
 * the tenant has not supplied a custom asset. Placed inside the TopNav by
 * the shell.
 */
export function TenantLogo({
  src,
  tenantName,
  className,
}: TenantLogoProps): ReactElement {
  const { t } = useTranslation();
  const alt =
    tenantName !== null && tenantName !== undefined && tenantName.length > 0
      ? t('shell.tenantLogo.altWithName', { tenantName })
      : t('shell.tenantLogo.altDefault');

  if (src !== null && src !== undefined && src.length > 0) {
    return (
      <img
        src={src}
        alt={alt}
        className={cn('h-7 w-auto object-contain', className)}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={alt}
      className={cn('inline-flex items-center', className)}
    >
      <Dna className="h-7 w-7" aria-hidden="true" />
    </span>
  );
}
